import axiosInstance from '@/utils/axiosInstance';
import { API_URL, USER_ROLES } from '@/constant/constant';

const getAuthHeaders = (vendorId) => ({
  sessionToken: localStorage.getItem('sessionToken'),
  'X-USER-ROLE': USER_ROLES.VENDOR,
  'X-Vendor-Id': vendorId,
});

/**
 * Fetch all charges for a vendor
 * @param {string} vendorId - The vendor ID
 * @returns {Promise<Array>} Array of charges
 */
export const fetchVendorCharges = async (vendorId) => {
  try {
    if (!vendorId) {
      console.warn('vendorId is required to fetch charges');
      return [];
    }

    const response = await axiosInstance.get(
      `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/charges?vendorId=${vendorId}`,
      {
        headers: getAuthHeaders(vendorId),
      }
    );
    return Array.isArray(response.data) ? response.data : [];
  } catch (error) {
    console.error('Error fetching charges:', error);
    throw error;
  }
};

/**
 * Create a new charge for a vendor
 * @param {string} vendorId - The vendor ID
 * @param {Object} payload - Charge data
 * @returns {Promise<Object>} Created charge data
 */
export const createVendorCharge = async (vendorId, payload) => {
  try {
    if (!vendorId) {
      throw new Error('vendorId is required');
    }

    const response = await axiosInstance.post(
      `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/charge?vendorId=${vendorId}`,
      payload,
      {
        headers: getAuthHeaders(vendorId),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error creating charge:', error);
    throw error;
  }
};

/**
 * Update an existing charge
 * @param {string} vendorId - The vendor ID
 * @param {string} chargeId - The charge ID to update
 * @param {Object} payload - Updated charge data
 * @returns {Promise<Object>} Updated charge data
 */
export const updateVendorCharge = async (vendorId, chargeId, payload) => {
  try {
    if (!vendorId || !chargeId) {
      throw new Error('vendorId and chargeId are required');
    }

    const response = await axiosInstance.put(
      `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/charge/${chargeId}?vendorId=${vendorId}`,
      payload,
      {
        headers: getAuthHeaders(vendorId),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error updating charge:', error);
    throw error;
  }
};

/**
 * Delete a charge
 * @param {string} vendorId - The vendor ID
 * @param {string} chargeId - The charge ID to delete
 * @returns {Promise<Object>} Response data
 */
export const deleteVendorCharge = async (vendorId, chargeId) => {
  try {
    if (!vendorId || !chargeId) {
      throw new Error('vendorId and chargeId are required');
    }

    const response = await axiosInstance.delete(
      `${API_URL.BASE_VENDOR}/rest/big-local/api/v1/vendor/charge/${chargeId}?vendorId=${vendorId}`,
      {
        headers: getAuthHeaders(vendorId),
      }
    );
    return response.data;
  } catch (error) {
    console.error('Error deleting charge:', error);
    throw error;
  }
};

/**
 * Validate charge data
 * @param {Object} charge - Charge to validate
 * @returns {Object} Validation result with isValid and message
 */
export const validateCharge = (charge) => {
  if (!charge.name || !charge.name.trim()) {
    return { isValid: false, message: 'Charge name is required' };
  }

  if (Number.isNaN(parseFloat(charge.value)) || parseFloat(charge.value) < 0) {
    return { isValid: false, message: 'Valid charge value is required' };
  }

  if (Number.isNaN(parseFloat(charge.actualValue)) || parseFloat(charge.actualValue) < 0) {
    return { isValid: false, message: 'Valid actual value is required' };
  }

  if (Number.isNaN(parseFloat(charge.vendorBears)) || parseFloat(charge.vendorBears) < 0) {
    return { isValid: false, message: 'Valid vendor bears value is required' };
  }

  return { isValid: true, message: 'Charge is valid' };
};
